// Utilitários para validação e formatação de telefone brasileiro
import * as yup from 'yup';

/**
 * Remove todos os caracteres não numéricos do telefone
 */
export const cleanPhoneNumber = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

/**
 * Formata um número de telefone brasileiro
 * @param phone - Número de telefone (com ou sem formatação)
 * @returns Telefone formatado (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
 */
export const formatPhoneNumber = (phone: string): string => {
  const cleaned = cleanPhoneNumber(phone);
  
  if (cleaned.length === 0) return '';
  
  // Se tem 11 dígitos (celular com 9)
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  
  // Se tem 10 dígitos (telefone fixo)
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  
  // Para números parciais, formata conforme vai digitando
  if (cleaned.length <= 2) {
    return cleaned;
  }
  
  if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  }
  
  if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  
  return phone; // Retorna o original se não conseguir formatar
};

/**
 * Valida se um número de telefone brasileiro é válido
 * @param phone - Número de telefone
 * @returns Objeto com isValid e message
 */
export const validatePhoneNumber = (phone: string): { isValid: boolean; message: string } => {
  const cleaned = cleanPhoneNumber(phone);
  
  // Verifica se está vazio
  if (!cleaned || cleaned.length === 0) {
    return { isValid: false, message: 'Telefone é obrigatório' };
  }
  
  // Verifica se tem apenas números
  if (!/^\d+$/.test(cleaned)) {
    return { isValid: false, message: 'Telefone deve conter apenas números' };
  }
  
  // Verifica o tamanho
  if (cleaned.length < 10) {
    return { isValid: false, message: 'Telefone deve ter pelo menos 10 dígitos' };
  }
  
  if (cleaned.length > 11) {
    return { isValid: false, message: 'Telefone deve ter no máximo 11 dígitos' };
  }
  
  // Verifica se tem 11 dígitos (celular)
  if (cleaned.length === 11) {
    // Verifica se o 9º dígito é 9 (celular)
    if (cleaned[2] !== '9') {
      return { isValid: false, message: 'Celular deve começar com 9 após o DDD' };
    }
    
    // Verifica se o DDD é válido (11-99)
    const ddd = parseInt(cleaned.slice(0, 2));
    if (ddd < 11 || ddd > 99) {
      return { isValid: false, message: 'DDD inválido' };
    }
    
    // Verifica se o número não é uma sequência
    if (/(\d)\1{8,}/.test(cleaned)) {
      return { isValid: false, message: 'Número de telefone inválido' };
    }
    
    return { isValid: true, message: 'Telefone válido' };
  }
  
  // Verifica se tem 10 dígitos (fixo)
  if (cleaned.length === 10) {
    // Verifica se o DDD é válido
    const ddd = parseInt(cleaned.slice(0, 2));
    if (ddd < 11 || ddd > 99) {
      return { isValid: false, message: 'DDD inválido' };
    }
    
    // Verifica se o primeiro dígito do número é válido para fixo (2-5)
    const firstDigit = parseInt(cleaned[2]);
    if (firstDigit < 2 || firstDigit > 5) {
      return { isValid: false, message: 'Número de telefone fixo inválido' };
    }
    
    // Verifica se o número não é uma sequência
    if (/(\d)\1{8,}/.test(cleaned)) {
      return { isValid: false, message: 'Número de telefone inválido' };
    }
    
    return { isValid: true, message: 'Telefone válido' };
  }
  
  return { isValid: false, message: 'Formato de telefone inválido' };
};

/**
 * Regex para validação de telefone brasileiro
 */
export const phoneRegex = /^\(?([1-9]{2})\)? ?(?:([2-5])([0-9]{3})|(9[1-9])([0-9]{3}))-?([0-9]{4})$/;

/**
 * Máscara de input para telefone
 */
export const phoneMask = '(99) 99999-9999';

/**
 * Função para aplicar máscara durante a digitação
 */
export const applyPhoneMask = (value: string): string => {
  const cleaned = cleanPhoneNumber(value);
  
  if (cleaned.length === 0) return '';
  
  if (cleaned.length <= 2) {
    return cleaned;
  }
  
  if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  }
  
  if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  
  // Limita a 11 dígitos
  return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
};

/**
 * Validação para Yup
 */
export const phoneValidation = (message: string = 'Telefone inválido') => {
  return (value: string) => {
    const validation = validatePhoneNumber(value);
    return validation.isValid || new yup.ValidationError(message, value, 'phone');
  };
};

export const buildWhatsAppPhoneNumber = (phone: string): string | null => {
  const cleaned = cleanPhoneNumber(phone);
  if (cleaned.length < 10) return null;
  if (cleaned.startsWith('55') && cleaned.length >= 12) return cleaned;
  if (cleaned.length === 10 || cleaned.length === 11) return `55${cleaned}`;
  if (cleaned.length >= 12) return cleaned;
  return null;
};

export const buildWhatsAppUrl = (phone: string, message?: string): string | null => {
  const waPhone = buildWhatsAppPhoneNumber(phone);
  if (!waPhone) return null;
  const base = `https://wa.me/${waPhone}`;
  if (!message?.trim()) return base;
  return `${base}?text=${encodeURIComponent(message.trim())}`;
};

/**
 * Exemplos de telefones válidos:
 * - (11) 98765-4321 (celular)
 * - (11) 3456-7890 (fixo)
 * - 11987654321 (sem formatação)
 * - 1134567890 (sem formatação)
 */
